import { useSuspenseQuery } from '@apollo/client/react';
import { useTransition } from 'react';

import { createRequiredContext } from 'src/context/createRequiredContext';
import { AuditLogsDocument } from 'src/graphql/actions/auditLogs/auditLogs.query';

import { useAuditLogsSlice, filterKeyToEventTypes, type IAuditLogNode } from './';

import { computeCreatedAfter } from '../lib';

type IContextValue = ReturnType<typeof useAuditLogsSlice> & {
  nodes: IAuditLogNode[];
  pagesCount: number;
  nodesCount: number;
  isLoading: boolean;
  startTransition: React.TransitionStartFunction;
};

const [useAuditLogs, AuditLogsProvider] = createRequiredContext<IContextValue>();

const AuditLogsContextProvider = ({ children }: React.PropsWithChildren) => {
  const [isLoading, startTransition] = useTransition();
  const slice = useAuditLogsSlice();
  const variables = {
    page: slice.page,
    ...(slice.eventType && { eventTypes: [...filterKeyToEventTypes[slice.eventType]] }),
    ...(slice.dateRange && { createdAfter: computeCreatedAfter(slice.dateRange) }),
  };
  const { data } = useSuspenseQuery(AuditLogsDocument, {
    variables,
    fetchPolicy: 'cache-and-network',
  });
  const { nodes, nodesCount, pagesCount } = data.auditLogs;

  return (
    <AuditLogsProvider value={{ ...slice, nodes, pagesCount, nodesCount, isLoading, startTransition }}>
      {children}
    </AuditLogsProvider>
  );
};

export { useAuditLogs, AuditLogsContextProvider };
